import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { LayoutDashboard, Mic, LogOut, ChevronRight, ChevronDown, User, Users, Settings, Brain, CreditCard, Clock, HeartPulse, Package, Building2, Gift, Palette, Layers, Shield, ShieldCheck, ShieldAlert, Wallet, Stethoscope, Wrench, Briefcase, BarChart3, Sun, Moon, Plug, Menu, UploadCloud, Search, Gauge, Repeat, Filter, Megaphone, Handshake, FileText, Tag, ToggleRight, Bell, Download, Map, Activity, CalendarClock, TestTube, Pill } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { bannerApi } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { useThemeStore } from '@/store/themeStore'
import { useDraftsStore } from '@/store/draftsStore'
import { useBranding } from '@/components/BrandingProvider'
import ScribenzaLogo from '@/components/ScribenzaLogo'
import toast from 'react-hot-toast'
import Tooltip from '@/components/Tooltip'
import ConfirmModal from '@/components/ConfirmModal'

const navGroups = [
  {
    key: 'clinical',
    label: 'Clinical',
    icon: Stethoscope,
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/meetings', icon: Mic, label: 'Sessions' },
      { to: '/drafts', icon: UploadCloud, label: 'Pending Uploads' },
      { to: '/patients', icon: Users, label: 'Patients' },
      { to: '/clinical-flags', icon: ShieldAlert, label: 'Clinical Flags' },
      { to: '/scheduling', icon: CalendarClock, label: 'Scheduling' },
      { to: '/lab-orders', icon: TestTube, label: 'Lab Orders' },
      { to: '/prescriptions', icon: Pill, label: 'Prescriptions' },
      { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    ],
  },
  {
    key: 'config',
    label: 'Configuration',
    icon: Wrench,
    items: [
      { to: '/sections', icon: Layers, label: 'Sections' },
      { to: '/parameters', icon: Settings, label: 'Parameters' },
      { to: '/templates', icon: Package, label: 'Templates' },
      { to: '/ai-settings', icon: Brain, label: 'AI Settings' },
    ],
  },
  {
    key: 'practice',
    label: 'Practice',
    icon: Briefcase,
    items: [
      { to: '/billing', icon: CreditCard, label: 'Billing' },
      { to: '/clinic', icon: Building2, label: 'Clinic' },
      { to: '/referrals', icon: Gift, label: 'Referrals' },
      { to: '/white-label', icon: Palette, label: 'White-label' },
    ],
  },
]

const adminNavItems = [
 { to: '/admin', icon: Gauge, label: 'Dashboard', exact: true },
 { to: '/admin/roadmap', icon: Map, label: 'Roadmap' },
 { to: '/admin/revenue', icon: BarChart3, label: 'Revenue' },
 { to: '/admin/ai-usage', icon: Brain, label: 'AI Usage' },
 { to: '/admin/productivity', icon: Activity, label: 'Productivity' },
 { to: '/admin/subscription-lifecycle', icon: Repeat, label: 'Subscriptions' },
 { to: '/admin/onboarding-funnel', icon: Filter, label: 'Onboarding' },
 { to: '/admin/system-health', icon: HeartPulse, label: 'System Health' },
 { to: '/admin/hipaa', icon: ShieldCheck, label: 'HIPAA' },
 { to: '/admin/broadcast', icon: Megaphone, label: 'Broadcast' },
 { to: '/admin/banners', icon: Bell, label: 'Banners' },
 { to: '/admin/users', icon: Users, label: 'Users' },
 { to: '/admin/bulk-actions', icon: Layers, label: 'Bulk Actions' },
 { to: '/admin/templates', icon: FileText, label: 'Templates' },
 { to: '/admin/partners', icon: Handshake, label: 'Partners' },
 { to: '/admin/pricing', icon: Tag, label: 'Pricing' },
 { to: '/admin/feature-flags', icon: ToggleRight, label: 'Feature Flags' },
 { to: '/admin/data-export', icon: Download, label: 'Data Export' },
 { to: '/admin/audit-logs', icon: Shield, label: 'Audit Logs' },
 { to: '/admin/payments', icon: Wallet, label: 'Payments' },
 // Phase 1 ships international-only — UPI admin surfaces are hidden
 // from the sidebar but the routes (/admin/upi-payments, /admin/upi-accounts)
 // and pages are still wired and reachable via direct URL. Re-enable when
 // India launch is on the roadmap.
 // { to: '/admin/upi-payments', icon: IndianRupee, label: 'UPI Payments' },
 // { to: '/admin/upi-accounts', icon: QrCode, label: 'UPI Accounts' },
 { to: '/admin/payment-settings', icon: CreditCard, label: 'Payment Settings' },
 { to: '/admin/integration-settings', icon: Plug, label: 'Integrations' },
 { to: '/admin/security-settings', icon: Shield, label: 'Security' },
]

function getActiveGroup(pathname) {
  for (const group of navGroups) {
    if (group.items.some(item => pathname === item.to || pathname.startsWith(item.to + '/'))) {
      return group.key
    }
  }
  if (pathname.startsWith('/admin')) return 'admin'
  return 'clinical'
}

export default function AppLayout() {
 const [collapsed, setCollapsed] = useState(false)
 const [mobileOpen, setMobileOpen] = useState(false)
 const { user, logout, impersonation, exitImpersonation } = useAuthStore()
 const [bannerDismissed, setBannerDismissed] = useState(() => {
   try { return JSON.parse(sessionStorage.getItem('scribenza_banner_dismissed') || 'null') } catch { return null }
 })
 const { data: bannerData } = useQuery({
   queryKey: ['active-banner'],
   queryFn: () => bannerApi.active().then(r => r.data.banner),
   refetchInterval: 5 * 60 * 1000,
   staleTime: 60 * 1000,
 })
 const banner = bannerData && bannerDismissed !== bannerData.id ? bannerData : null
 const dismissBanner = () => {
   if (bannerData) {
     sessionStorage.setItem('scribenza_banner_dismissed', JSON.stringify(bannerData.id))
     setBannerDismissed(bannerData.id)
   }
 }
 const BANNER_STYLE = {
   info:    { bg: '#06B6D420', border: '#06B6D460', text: '#67E8F9' },
   success: { bg: '#10B98120', border: '#10B98160', text: '#6EE7B7' },
   warning: { bg: '#F59E0B20', border: '#F59E0B60', text: '#FCD34D' },
   danger:  { bg: '#EF444420', border: '#EF444460', text: '#FCA5A5' },
 }
 const { theme, toggle: toggleTheme } = useThemeStore()
 const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
 const navigate = useNavigate()
 const location = useLocation()
 const branding = useBranding()
 const [expandedGroup, setExpandedGroup] = useState(() => getActiveGroup(location.pathname))
 const pendingDrafts = useDraftsStore(s => s.drafts.length)
 const initDrafts = useDraftsStore(s => s.init)

 useEffect(() => { initDrafts() }, [initDrafts])

 useEffect(() => {
   setExpandedGroup(getActiveGroup(location.pathname))
 }, [location.pathname])

 const toggleGroup = (key) => {
   setExpandedGroup(prev => prev === key ? null : key)
 }

 const handleLogout = async () => {
   await logout()
   toast.success('Logged out successfully')
   navigate('/login')
 }

 function Sidebar({ mobile = false }) {
 return (
 <aside className={`flex flex-col h-full bg-dark-900 border-r border-dark-700 transition-all duration-200
 ${mobile ? 'w-72' : collapsed ? 'w-16' : 'w-60'}`}>

 <div className={`flex flex-col items-center gap-0 px-3 py-4 border-b border-dark-700 ${collapsed && !mobile ? 'justify-center px-2' : ''}`}>
 {/* The collapsed rail is w-16, too narrow for the 120px wordmark. It was
     first square-cropped with object-cover, but cropping at the container
     edge still reads as a rendering fault, so the rail now uses the
     purpose-made square mark asset instead — padded inside its own canvas,
     nothing clipped. A white-label logo is already square; it just shrinks. */}
 {branding?.logo_url
   ? <img src={branding.logo_url} alt={branding.brand_name}
       className={`rounded-lg object-contain flex-shrink-0 ${collapsed && !mobile ? 'w-10 h-10' : 'w-12 h-12'}`} />
   : <ScribenzaLogo alt="Scribenza" adaptToLightTheme
       variant={collapsed && !mobile ? 'mark' : 'wordmark'}
       className={`rounded-lg flex-shrink-0 object-contain ${collapsed && !mobile ? 'w-10 h-10' : 'w-[120px] h-10'}`} />}
 {(!collapsed || mobile) && (
 <div>
 {/* Show the clinic's brand name when white-label is applied — QA
     filed 'branding not applied after save' because the previous
     build had this line commented out, so the header always read
     'Scribenza' regardless of the saved config. Fall through to
     the wordmark logo (which already renders above) by only
     printing the brand text when it's a real override. */}
 {branding?.brand_name && (
   <div className="text-white font-semibold text-lg leading-tight text-center">{branding.brand_name}</div>
 )}
 <div className="text-dark-500 text-[10.5px] mt-0.5 tracking-wide">{branding?.tagline || 'Clinical Documentation AI'}</div>
 </div>
 )}
 </div>

 <div className={`px-3 py-4 space-y-2 ${collapsed && !mobile ? 'flex flex-col items-center' : ''}`}>
 <button onClick={() => { navigate('/meetings/new'); setMobileOpen(false) }}
 className="w-full justify-center text-sm gap-2 py-2.5 rounded-lg font-semibold
 text-white border-none cursor-pointer flex items-center transition-all hover:opacity-90"
 style={{
   // Prefer the clinic's white-label primary/secondary when
   // BrandingProvider set them; the CSS custom-properties are
   // written on <html> in applyBranding(). Fall back to the
   // Scribenza default gradient so a clinic without white-label
   // still gets the original look.
   background: (branding?.colors?.primary && branding?.colors?.secondary)
     ? `linear-gradient(135deg, ${branding.colors.primary}, ${branding.colors.secondary})`
     : (theme === 'light' ? 'linear-gradient(135deg,#7C3AED,#3B82F6)' : 'linear-gradient(135deg,#533AB7,#185FA5)')
 }}>
 <Mic size={15} />
 {(!collapsed || mobile) && <span>New Session</span>}
 </button>
 <Tooltip content="Search sessions and patients" position="right" delay={100}>
 <button onClick={() => { navigate('/search'); setMobileOpen(false) }}
 className={`w-full justify-center text-sm gap-2 py-2 rounded-lg
 text-dark-300 bg-dark-800 border border-dark-700
 cursor-pointer flex items-center transition-all hover:bg-dark-700 hover:text-dark-100
 ${collapsed && !mobile ? 'px-0' : ''}`}>
 <Search size={14} />
 {(!collapsed || mobile) && <span className="text-xs font-medium">Search</span>}
 </button>
 </Tooltip>
 </div>

 <nav className="flex-1 px-2 space-y-1 overflow-y-auto">
 {navGroups.map(group => {
   const isExpanded = expandedGroup === group.key
   const hasActive = group.items.some(item =>
     location.pathname === item.to || location.pathname.startsWith(item.to + '/'))
   const GroupIcon = group.icon
   return (
     <div key={group.key}>
       {(!collapsed || mobile) ? (
         <button onClick={() => toggleGroup(group.key)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer border-none
            ${hasActive ? 'active-brand-light' : 'text-dark-400 hover:text-dark-200 hover:bg-dark-700/30'}`}>
           <GroupIcon size={14} className="flex-shrink-0" />
           <span className="flex-1 text-left">{group.label}</span>
           <ChevronDown size={12} className={`transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} />
         </button>
       ) : (
         <div className="w-full h-px bg-dark-700/50 my-2" />
       )}
       <div className={`space-y-0.5 overflow-hidden transition-all duration-200 ${
         (!collapsed || mobile) ? (isExpanded ? 'max-h-96 opacity-100 mt-0.5' : 'max-h-0 opacity-0') : ''}`}>
         {group.items.map(({ to, icon: Icon, label }) => {
           const showBadge = to === '/drafts' && pendingDrafts > 0
           return (
           <NavLink key={to} to={to} onClick={() => setMobileOpen(false)}
              title={collapsed && !mobile ? label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg text-sm font-medium transition-all
                ${isActive ? 'active-brand-light' : 'text-dark-400 hover:text-dark-100 hover:bg-dark-700/60'}
                ${collapsed && !mobile ? 'justify-center px-3 py-2.5' : 'pl-8 pr-3 py-2'}`}>
             <Tooltip content={label} position="right" delay={100}>
               <Icon size={16} className="flex-shrink-0" />
             </Tooltip>
             {(!collapsed || mobile) && <span className="flex-1">{label}</span>}
             {showBadge && (!collapsed || mobile) && (
               <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                 style={{ background: '#F59E0B', color: '#fff' }}>
                 {pendingDrafts}
               </span>
             )}
           </NavLink>
           )
         })}
       </div>
     </div>
   )
 })}

 {user?.role === 'platform_admin' && (
   <div>
     {(!collapsed || mobile) ? (
       <button onClick={() => toggleGroup('admin')}
         className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer border-none mt-1
         ${location.pathname.startsWith('/admin') ? 'text-purple-300' : 'text-dark-500 hover:text-dark-300 hover:bg-dark-800/30'}`}
         style={{ background: location.pathname.startsWith('/admin') ? '#533AB712' : 'transparent' }}>
         <Shield size={14} className="flex-shrink-0" />
         <span className="flex-1 text-left">Admin</span>
         <ChevronDown size={12} className={`transition-transform duration-200 ${expandedGroup === 'admin' ? '' : '-rotate-90'}`} />
       </button>
     ) : (
       <div className="w-full h-px bg-dark-700/50 my-2" />
     )}
     <div className={`space-y-0.5 overflow-hidden transition-all duration-200 ${
       (!collapsed || mobile) ? (expandedGroup === 'admin' ? 'max-h-96 opacity-100 mt-0.5' : 'max-h-0 opacity-0') : ''}`}>
       {adminNavItems.map(({ to, icon: Icon, label, exact }) => (
         <NavLink key={to} to={to} end={exact} onClick={() => setMobileOpen(false)}
              title={collapsed && !mobile ? label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg text-sm font-medium transition-all
                ${isActive ? 'active-brand-light' : 'text-dark-400 hover:text-dark-100 hover:bg-dark-700/60'}
                ${collapsed && !mobile ? 'justify-center px-3 py-2.5' : 'pl-8 pr-3 py-2'}`}>
           <Tooltip content={label} position="right" delay={100}>
             <Icon size={16} className="flex-shrink-0" />
           </Tooltip>
           {(!collapsed || mobile) && <span>{label}</span>}
         </NavLink>
       ))}
     </div>
   </div>
 )}
 </nav>

 <div className={`border-t border-dark-700 p-3 ${collapsed && !mobile ? 'flex flex-col items-center gap-2' : ''}`}>
 {(!collapsed || mobile) ? (
 <div className="space-y-1">
 <NavLink
   to="/profile"
   onClick={() => mobile && setMobileOpen(false)}
   className={({ isActive }) =>
     `flex items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium transition-all
     ${isActive ? 'active-brand-light' : 'text-dark-400 hover:text-dark-100 hover:bg-dark-700/60'}`}
 >
   <User size={15} className="flex-shrink-0" />
   <span>My Profile</span>
 </NavLink>
 <NavLink
   to="/settings/integrations"
   onClick={() => mobile && setMobileOpen(false)}
   className={({ isActive }) =>
     `flex items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium transition-all
     ${isActive ? 'active-brand-light' : 'text-dark-400 hover:text-dark-100 hover:bg-dark-700/60'}`}
 >
   <CalendarClock size={15} className="flex-shrink-0" />
   <span>Connected Calendars</span>
 </NavLink>
 <div className="flex items-center gap-3 pt-1">
 <NavLink
   to="/profile"
   onClick={() => mobile && setMobileOpen(false)}
   className="flex items-center gap-3 flex-1 min-w-0 rounded-lg -ml-1 px-1 py-1 hover:bg-dark-700/60 transition-colors"
 >
 <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white initials-text flex-shrink-0"
 style={{ background: theme === 'light' ? 'linear-gradient(135deg,#7C3AED,#3B82F6)' : 'linear-gradient(135deg,#533AB7,#185FA5)' }}>
 {user?.initials || <User size={14} />}
 </div>
 <div className="flex-1 min-w-0">
 <div className="text-sm font-medium text-dark-100 truncate">{user?.name}</div>
 <div className="text-xs text-dark-500 truncate">{user?.email}</div>
 </div>
 </NavLink>
 <Tooltip content={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
   <button onClick={toggleTheme} className="text-dark-400 hover:text-dark-200 transition-colors p-1">
     {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
   </button>
 </Tooltip>
 <Tooltip content="Sign Out">
   <button aria-label="Log out" onClick={() => setShowLogoutConfirm(true)} className="text-dark-400 hover:text-red-400 transition-colors p-1">
     <LogOut size={14} />
   </button>
 </Tooltip>
 </div>
 </div>
 ) : (
 <>
 <Tooltip content="My Profile" position="right">
   <NavLink to="/profile" className="text-dark-400 hover:text-dark-200 transition-colors p-2 block">
     <User size={15} />
   </NavLink>
 </Tooltip>
 <Tooltip content={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'} position="right">
   <button onClick={toggleTheme} className="text-dark-400 hover:text-dark-200 transition-colors p-2">
     {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
   </button>
 </Tooltip>
 <Tooltip content="Sign Out" position="right">
   <button aria-label="Log out" onClick={() => setShowLogoutConfirm(true)} className="text-dark-400 hover:text-red-400 transition-colors p-2">
     <LogOut size={15} />
   </button>
 </Tooltip>
 </>
 )}
 </div>
 </aside>
 )
 }

 return (
 <div className="flex h-screen bg-dark-950 overflow-hidden">
 <div className="hidden md:flex flex-col relative flex-shrink-0">
 <Sidebar />
 <button aria-label="Toggle sidebar" onClick={() => setCollapsed(!collapsed)}
  className="absolute -right-3 top-8 w-6 h-6 bg-dark-700 border border-dark-600 rounded-full
  flex items-center justify-center text-dark-400 hover:text-dark-100 transition-colors z-10">
 <ChevronRight size={12} className={`transition-transform ${collapsed ? '' : 'rotate-180'}`} />
 </button>
 </div>
 {mobileOpen && (
 <div className="fixed inset-0 z-50 md:hidden flex">
 <div className="flex-shrink-0"><Sidebar mobile /></div>
 <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
 </div>
 )}
 <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
 <div className="md:hidden flex items-center justify-between px-4 py-2 bg-dark-900 border-b border-dark-700">
 <button aria-label="Open navigation menu" onClick={() => setMobileOpen(true)} className="text-dark-400 hover:text-dark-100"><Menu size={30} /></button>
 <div className="flex items-center gap-2">
 <ScribenzaLogo alt="Scribenza" adaptToLightTheme className="w-[120px] h-10 rounded object-contain" />
 {/* <span className="font-semibold text-white text-xl">Scribenza</span> */}
 </div>
 <div className="w-8" />
 </div>
  {banner && (() => {
    const s = BANNER_STYLE[banner.severity] || BANNER_STYLE.info
    return (
     <div className="flex items-center justify-center gap-3 px-4 py-2 text-xs font-medium"
          style={{ background: s.bg, borderBottom: `1px solid ${s.border}`, color: s.text }}>
       <strong>{banner.title}</strong>
       <span className="opacity-80">·</span>
       <span className="opacity-90">{banner.message}</span>
       {banner.cta_url && banner.cta_label && (
         <a href={banner.cta_url} target="_blank" rel="noopener noreferrer"
            className="px-2 py-0.5 rounded font-semibold underline hover:no-underline" style={{ color: s.text }}>
           {banner.cta_label} →
         </a>
       )}
       {banner.is_dismissible && (
         <button onClick={dismissBanner} className="ml-2 opacity-60 hover:opacity-100 cursor-pointer"
                 style={{ color: s.text, background: 'transparent', border: 'none' }}>
           ×
         </button>
       )}
     </div>
    )
  })()}
  {impersonation && (
   <div className="flex items-center justify-center gap-3 px-4 py-2 text-xs font-medium"
        style={{ background: 'linear-gradient(135deg,#EC489920,#7C3AED20)', borderBottom: '1px solid #EC489960' }}>
     <Shield size={12} className="text-pink-300" />
     <span className="text-pink-200">
       Impersonating <strong className="text-white">{user?.name}</strong>{' '}
       <span className="text-pink-400/70">·</span>{' '}
       Admin: <span className="text-pink-100">{impersonation.admin?.email}</span>
     </span>
     <button
       onClick={async () => { await exitImpersonation(); navigate('/admin/users'); toast.success('Exited impersonation') }}
       className="px-2 py-0.5 rounded bg-pink-600/30 hover:bg-pink-600/50 text-pink-100 transition-colors">
       Exit
     </button>
   </div>
  )}
  {user?.on_trial && (
  <div className="flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium"
  style={{ background: 'linear-gradient(135deg,#533AB715,#185FA515)', borderBottom: '1px solid #533AB730' }}>
  <Clock size={12} className={user.trial_days_left > 0 ? "text-purple-400" : "text-red-400"} />
  <span className={user.trial_days_left > 0 ? "text-purple-300" : "text-red-400"}>
    {user.trial_days_left > 0 ? (
      <>Free trial: <strong className="text-white">{user.trial_days_left} day{user.trial_days_left !== 1 ? 's' : ''}</strong> remaining</>
    ) : (
      <strong>Trial expired</strong>
    )}
  </span>
  </div>
  )}
 <main className="flex-1 min-w-0 overflow-auto flex flex-col">
   <div className="flex-1 min-w-0 w-full"><Outlet /></div>
   {/* ── Copyright footer ───────────────────────────────── */}
    <footer className="flex-shrink-0 border-t border-dark-700/60 bg-dark-900 px-4 sm:px-6 py-[21.1px]">
     <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px] text-dark-400 font-medium">
          {branding?.footer_text || `© ${new Date().getFullYear()} Scribenza. All rights reserved.`}
        </p>
       <div className="flex items-center gap-4">
          <a href="/privacy" target="_blank" rel="noopener noreferrer"
            className="text-[12px] text-dark-500 hover:text-dark-200 transition-colors">
            Privacy Policy
          </a>
          <a href="/terms" target="_blank" rel="noopener noreferrer"
            className="text-[12px] text-dark-500 hover:text-dark-200 transition-colors">
            Terms of Service
          </a>
       </div>
     </div>
   </footer>
 </main>
 </div>

 <ConfirmModal 
   isOpen={showLogoutConfirm}
   onClose={() => setShowLogoutConfirm(false)}
   onConfirm={handleLogout}
   title="Sign Out"
   message="Are you sure you want to sign out of your account?"
   confirmText="Sign Out"
   type="danger"
 />
 </div>
 )
}
